/**
 * i18next configuration for LexIDE — Phase A i18n foundation.
 *
 * Three languages today (pt-BR / en-US / es-ES) matching the SUPPORTED_LANGUAGES
 * list in `@/lib/language`. Translations live as JSON namespaces under
 * `./locales/<tag>.json` and are loaded eagerly (small payloads; lazy loading
 * is a Phase B concern once the catalog grows).
 *
 * The active language is driven by `useLanguageStore` — i18next's `language`
 * stays in lockstep with `useLanguageStore.lang` via a subscription wired in
 * `setUpI18nStoreBridge()`. The store itself resolves the boot value from
 * (1) the server-provided locale (auth-exchange response — see
 * `@/lib/lexiun-jwt`), (2) localStorage, (3) navigator.language, (4) pt-BR.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import ptBR from "./locales/pt-BR.json";
import enUS from "./locales/en-US.json";
import esES from "./locales/es-ES.json";

import { useLanguageStore } from "@/stores/useLanguageStore";
import type { LanguagePreference } from "@/lib/language";

export const I18N_RESOURCES = {
  "pt-BR": { translation: ptBR },
  "en-US": { translation: enUS },
  "es-ES": { translation: esES },
} as const;

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: I18N_RESOURCES,
    fallbackLng: "pt-BR",
    supportedLngs: ["pt-BR", "en-US", "es-ES"],
    interpolation: { escapeValue: false }, // React already escapes
    detection: {
      // useLanguageStore handles persistence; the LanguageDetector chain here
      // is only a fallback before the store hydrates.
      order: ["navigator"],
      caches: [],
    },
    // Bootstrap with the store's current value (which may already reflect a
    // navigator-derived choice from `lib/language.bootLang`).
    lng: useLanguageStore.getState().lang,
  });

let bridgeInstalled = false;

/**
 * Subscribe i18next to `useLanguageStore` so any `setLang(...)` call from the
 * settings UI, the server-locale hydration, or programmatic switching also
 * flips the i18next active language. Idempotent — safe to call multiple times.
 */
export function setUpI18nStoreBridge(): void {
  if (bridgeInstalled) return;
  bridgeInstalled = true;
  useLanguageStore.subscribe((state, prev) => {
    if (state.lang === prev.lang) return;
    if (i18n.language !== state.lang) {
      void i18n.changeLanguage(state.lang as LanguagePreference);
    }
  });
}

export default i18n;
